// utils/brandTerms.ts
//
// Product terms that stay Latin in the source copy ("Astria", "Life GPS", "Pro")
// but should read natively in every language. Applied as a final pass over
// translated text, so individual strings and locale files don't each have to
// remember it. Edit the tables below to change a transliteration everywhere.
//
// Latin-script languages keep "Astria" and "Pro" as written (they are the same
// word there); only "Life GPS" is localized. "GPS" is an acronym and stays.

interface BrandTerms {
  /** Replaces "Astria"; omitted where the Latin spelling is correct. */
  astria?: string;
  /** Replaces "Pro" (the plan tier); omitted where the Latin spelling is correct. */
  pro?: string;
  /** Replaces "Life GPS" / "Life-GPS". */
  lifeGps: string;
}

const BRAND_TERMS: Readonly<Record<string, BrandTerms>> = {
  th: { astria: "แอสเตรีย", pro: "โปร", lifeGps: "ไลฟ์ GPS" },
  ko: { astria: "아스트리아", pro: "프로", lifeGps: "라이프 GPS" },
  ja: { astria: "アストリア", pro: "プロ", lifeGps: "ライフGPS" },
  zh: { astria: "阿斯特里亚", pro: "专业版", lifeGps: "人生 GPS" },
  hi: { astria: "आस्त्रिया", pro: "प्रो", lifeGps: "लाइफ GPS" },
  mr: { astria: "आस्त्रिया", pro: "प्रो", lifeGps: "लाइफ GPS" },
  ta: { astria: "அஸ்த்ரியா", pro: "புரோ", lifeGps: "லைஃப் GPS" },
  ar: { astria: "أستريا", pro: "برو", lifeGps: "GPS الحياة" },
  ru: { astria: "Астрия", pro: "Про", lifeGps: "GPS жизни" },
  de: { lifeGps: "Lebens-GPS" },
  es: { lifeGps: "GPS de vida" },
  fr: { lifeGps: "GPS de vie" },
  pt: { lifeGps: "GPS da vida" },
  it: { lifeGps: "GPS di vita" },
  id: { lifeGps: "GPS Kehidupan" },
  ms: { lifeGps: "GPS Kehidupan" },
  vi: { lifeGps: "GPS Cuộc đời" },
  fil: { lifeGps: "GPS ng Buhay" },
};

// "Astria" but not in a hashtag, an email or a web address (Astria.com).
const ASTRIA = /(?<![#@\w.])Astria(?![\w]|\.(?:com|fyi|app|io|ai)\b)/g;
const LIFE_GPS = /Life[ -]GPS/g;
const PRO = /(?<![\w-])Pro(?![\w-])/g;

/** Base language code: "pt-BR" -> "pt", "zh-CN" -> "zh". */
const baseLanguage = (lang?: string): string => (lang || "en").toLowerCase().split("-")[0];

/** Rewrites Astria / Life GPS / Pro in already-translated text for `lang`. */
export const localizeBrand = (text: string, lang?: string): string => {
  const terms = BRAND_TERMS[baseLanguage(lang)];
  if (!terms || !text) return text;
  let out = text.replace(LIFE_GPS, terms.lifeGps);
  if (terms.astria) out = out.replace(ASTRIA, terms.astria);
  if (terms.pro) out = out.replace(PRO, terms.pro);
  return out;
};
